import { GBasePass, GetBasePassUniformParametersList, Material } from "../../../basePass";
import {
    CreateBuffer,
    CreateFramebufferWithAttachment,
    CreateTextureRT,
    DefineVertexAttrib,
} from "../../../core/resourcesUtils";
import { Shader, ShaderTF } from "../../../core/shaderUtils";
import { GTexturePool } from "../../../core/texturePool";
import { GetVec2 } from "../../../core/types";
import { GTime } from "../../../core/utils";
import { GLBindUniformBuffer, GLSetTexture } from "../../../helpers/glHelper";
import { GPostProcessPasses } from "../../../renderingMain";
import { CommonRenderingResources } from "../../../shaders/shaderConfig";
import {
    GetDestDecalUpdateShaderVS,
    GetProcTileUpdatePS,
    GetShaderSourceBasePassDecalRenderVS,
    GetShaderSourceBasePassDestrRenderPS,
    GetShaderSourceBasePassProceduralTileRenderVS,
    scGetDecalSpecificPSCode,
    scGetProcTileSpecificPSCode,
} from "../../../shaders/shaderDestrTiles";

import { ParticleUpdatePS } from "../../../shaders/shaderParticles";
import { ShaderSourceFullscreenPassVS } from "../../../shaders/shaderPostProcess";
import { SceneBase } from "../../scene";
import { GBulletSystem } from "./bullets";
import { PayneSceneDestrPainting } from "./destrPaintingMain";

export class DestructionTiles {
    AreaSizeLength;
    TileSize;
    NumTiles;

    Material = new Material();

    TileHealthStateTexture: WebGLTexture[] = [];
    TileHealthStateTextureFBO: WebGLFramebuffer[] = [];
    CurPingPongTextureIndex = 0;

    //Shader
    ShaderRender: Shader | null = null;
    ShaderUpdate: ShaderTF | null = null;

    OnUpdate(gl: WebGL2RenderingContext) {
        //Collision Update
        //const bActive = GUserInputDesc.bPointerInputPressedCurFrame ;
        const bActive = GBulletSystem.GInstance!.bIntersectionThisFrame;
        if (bActive) {
            const curShader = this.ShaderUpdate!;

            gl.viewport(0, 0, this.NumTiles.x, this.NumTiles.y);
            gl.bindFramebuffer(gl.FRAMEBUFFER, this.TileHealthStateTextureFBO[1 - this.CurPingPongTextureIndex]);

            gl.useProgram(curShader!.ShaderProgram);
            gl.bindVertexArray(CommonRenderingResources.FullscreenPassVAO);

            const uniformLoc = curShader.UniformParametersLocationList;

            GLBindUniformBuffer(
                gl,
                SceneBase.UniformBuffer.BufferGPU!,
                curShader.ShaderProgram,
                uniformLoc.SceneUniformBuffer,
            );

            let textureBindIndex = 1;
            GLSetTexture(
                gl,
                uniformLoc.TileHealthStateTexture,
                this.TileHealthStateTexture[this.CurPingPongTextureIndex],
                textureBindIndex++,
            );

            gl.drawArrays(gl.TRIANGLES, 0, 3);

            this.CurPingPongTextureIndex = 1 - this.CurPingPongTextureIndex;
        }
    }

    Render(gl: WebGL2RenderingContext) {
        const curShader = this.ShaderRender!;
        gl.useProgram(curShader.ShaderProgram);

        const uniformLoc = curShader.UniformParametersLocationList;

        GLBindUniformBuffer(
            gl,
            SceneBase.UniformBuffer.BufferGPU!,
            curShader.ShaderProgram,
            uniformLoc.SceneUniformBuffer,
        );

        {
            const uniformsLocation = curShader.UniformParametersLocationList;
            let textureBindIndex = 1;
            gl.uniform1f(uniformsLocation.TextureScale, this.Material.TextureScale);
            gl.uniform1f(uniformsLocation.Brightness, this.Material.Brightness);
            gl.uniform1f(uniformsLocation.Metalness, this.Material.Metalness);
            gl.uniform1i(uniformsLocation.bEmitter, this.Material.bisEmmiter ? 1 : 0);

            GLSetTexture(gl, uniformsLocation.AlbedoTexture, this.Material.AlbedoTexture, textureBindIndex++);
            GLSetTexture(gl, uniformsLocation.NormalTexture, this.Material.NormalTexture, textureBindIndex++);
            GLSetTexture(gl, uniformsLocation.RoughnessTexture, this.Material.RoughnessTexture, textureBindIndex++);

            GLSetTexture(gl, uniformsLocation.MaskTexture, this.Material.MaskTexture, textureBindIndex++);

            GLSetTexture(
                gl,
                uniformsLocation.TileHealthStateTexture,
                this.TileHealthStateTexture[this.CurPingPongTextureIndex],
                textureBindIndex++,
            );

            GLSetTexture(
                gl,
                uniformsLocation.PrevFrameSceneColor,
                GPostProcessPasses.Bloom!.DownsampleRTMipArr![1],
                textureBindIndex++,
            );
            GLSetTexture(
                gl,
                uniformsLocation.PrevFrameSceneColorBlur,
                GPostProcessPasses.Bloom!.DownsampleRTMipArr![2],
                textureBindIndex++,
            );

            if (SceneBase.TextureEnvMapWithMips) {
                gl.activeTexture(gl.TEXTURE0 + textureBindIndex);
                gl.bindTexture(gl.TEXTURE_CUBE_MAP, SceneBase.TextureEnvMapWithMips);
                gl.uniform1i(uniformsLocation.CubemapTexture, textureBindIndex);
                textureBindIndex++;
            }
            GLSetTexture(gl, uniformsLocation.BRDF_LUT, GBasePass.GInstance!.TextureIntegratedBRDF, textureBindIndex++);
            gl.uniform1i(uniformsLocation.EUseLightprobe, 1);
            gl.uniform1i(uniformsLocation.LightprobeIndex, 0);
            GLSetTexture(
                gl,
                curShader.UniformParametersLocationList.SHCoefficientsTexture,
                SceneBase.LightprobesManager.TextureSHCoeff,
                textureBindIndex++,
            );
        }

        const numInstances = this.NumTiles.x * this.NumTiles.y;
        gl.drawArraysInstanced(gl.TRIANGLES, 0, 36, numInstances);
    }

    constructor(gl: WebGL2RenderingContext, scene: PayneSceneDestrPainting) {
        this.AreaSizeLength = GetVec2(24, 12);
        this.TileSize = GetVec2(0.1, 0.4);
        const tileSizeScale = 2.0;
        this.TileSize.x *= tileSizeScale;
        this.TileSize.y *= tileSizeScale;

        this.NumTiles = GetVec2(
            Math.ceil(this.AreaSizeLength.x / this.TileSize.x),
            Math.ceil(this.AreaSizeLength.y / this.TileSize.y),
        );

        //console.log(`Wall Tiles Num : ${this.NumTiles.x}, ${this.NumTiles.y}`);

        //State Texture
        {
            for (let i = 0; i < 2; i++) {
                this.TileHealthStateTexture[i] = CreateTextureRT(gl, this.NumTiles, gl.R8, gl.RED, gl.UNSIGNED_BYTE)!;
                this.TileHealthStateTextureFBO[i] = CreateFramebufferWithAttachment(
                    gl,
                    this.TileHealthStateTexture[i]!,
                )!;

                //Fill with 0s
                gl.viewport(0, 0, this.NumTiles.x, this.NumTiles.y);
                gl.bindFramebuffer(gl.FRAMEBUFFER, this.TileHealthStateTextureFBO[i]);
                gl.clearColor(1.0, 1.0, 1.0, 1.0);
                gl.clear(gl.COLOR_BUFFER_BIT);
            }
        }

        this.ShaderRender = new Shader(
            gl,
            GetShaderSourceBasePassProceduralTileRenderVS(this),
            GetShaderSourceBasePassDestrRenderPS(
                scene.SceneSDFDescShootingScene!,
                this.NumTiles,
                this.TileSize,
                scGetProcTileSpecificPSCode(),
            ),
            GetBasePassUniformParametersList,
        );

        this.ShaderUpdate = new Shader(
            gl,
            ShaderSourceFullscreenPassVS,
            GetProcTileUpdatePS(this.NumTiles, this.TileSize),
            GetBasePassUniformParametersList,
        );

        const matName = `woodTiles2`;
        this.Material.InitTextures(gl, matName + `DFS`, matName + `NRM`, matName + `RGH_R8`, false);
        this.Material.MaskTexture = GTexturePool.CreateTexture(gl, false, `woodRoughDFS`, true, true);
    }
}

export class Decals {
    MaxNumInstances = 500;
    LastSpawnTime = 0;
    CurSpawnHeadIndex = 0;
    CurBufferIndex = 0;

    PositionsBufferGPU: WebGLBuffer[] = [];
    TransformFeedbackGPU: WebGLTransformFeedback[] = [];

    Material = new Material();

    //Shaders
    ShaderUpdate: ShaderTF | null = null;
    ShaderRender: Shader | null = null;

    VAOUpdate: WebGLVertexArrayObject[] = [];
    VAORender: WebGLVertexArrayObject[] = [];

    OnUpdate(gl: WebGL2RenderingContext) {
        //const timeSinceLastSpawn = GTime.Cur - this.LastSpawnTime;
        //const bSpawnParticlesThisFrame = GUserInputDesc.bPointerInputPressedCurFrame && timeSinceLastSpawn > 0.1;
        const bSpawnParticlesThisFrame = GBulletSystem.GInstance!.bIntersectionThisFrame;

        if (bSpawnParticlesThisFrame) {
            this.LastSpawnTime = GTime.Cur;

            const curOffset = this.CurSpawnHeadIndex;

            this.CurSpawnHeadIndex += 1;
            if (this.CurSpawnHeadIndex > this.MaxNumInstances - 1) {
                this.CurSpawnHeadIndex = 0;
            }

            //Spawn decals
            {
                const curShader = this.ShaderUpdate!;
                gl.bindFramebuffer(gl.FRAMEBUFFER, null);
                gl.bindRenderbuffer(gl.RENDERBUFFER, null);

                gl.enable(gl.RASTERIZER_DISCARD);

                gl.useProgram(curShader!.ShaderProgram);
                gl.bindVertexArray(this.VAOUpdate[this.CurBufferIndex]);

                const uniformLoc = curShader.UniformParametersLocationList;

                gl.uniform2i(uniformLoc.ParticleCurSpawnIndexRange, curOffset, curOffset + 1);

                GLBindUniformBuffer(
                    gl,
                    SceneBase.UniformBuffer.BufferGPU!,
                    curShader.ShaderProgram,
                    uniformLoc.SceneUniformBuffer,
                );

                gl.bindTransformFeedback(gl.TRANSFORM_FEEDBACK, this.TransformFeedbackGPU[1 - this.CurBufferIndex]);

                gl.beginTransformFeedback(gl.POINTS);
                gl.drawArrays(gl.POINTS, 0, this.MaxNumInstances);
                gl.endTransformFeedback();
                gl.disable(gl.RASTERIZER_DISCARD);

                gl.bindTransformFeedback(gl.TRANSFORM_FEEDBACK, null);

                this.CurBufferIndex = 1 - this.CurBufferIndex;
            }
        }
    }

    Render(gl: WebGL2RenderingContext, parentTiles: DestructionTiles) {
        const curShader = this.ShaderRender!;
        gl.useProgram(curShader.ShaderProgram);
        gl.bindVertexArray(this.VAORender[this.CurBufferIndex]);

        const uniformsLocation = curShader.UniformParametersLocationList;

        GLBindUniformBuffer(
            gl,
            SceneBase.UniformBuffer.BufferGPU!,
            curShader.ShaderProgram,
            uniformsLocation.SceneUniformBuffer,
        );

        {
            let textureBindIndex = 1;
            gl.uniform1f(uniformsLocation.TextureScale, this.Material.TextureScale);
            gl.uniform1f(uniformsLocation.Brightness, this.Material.Brightness);
            gl.uniform1f(uniformsLocation.Metalness, this.Material.Metalness);
            gl.uniform1i(uniformsLocation.bEmitter, this.Material.bisEmmiter ? 1 : 0);
            gl.uniform1i(uniformsLocation.bHasMask, this.Material.bHasMask ? 1 : 0);

            GLSetTexture(gl, uniformsLocation.AlbedoTexture, this.Material.AlbedoTexture, textureBindIndex++);
            GLSetTexture(gl, uniformsLocation.NormalTexture, this.Material.NormalTexture, textureBindIndex++);
            GLSetTexture(gl, uniformsLocation.RoughnessTexture, this.Material.RoughnessTexture, textureBindIndex++);
            GLSetTexture(gl, uniformsLocation.MaskTexture, this.Material.MaskTexture, textureBindIndex++);
            GLSetTexture(
                gl,
                uniformsLocation.TileHealthStateTexture,
                parentTiles.TileHealthStateTexture[parentTiles.CurPingPongTextureIndex],
                textureBindIndex++,
            );

            if (SceneBase.TextureEnvMapWithMips) {
                gl.activeTexture(gl.TEXTURE0 + textureBindIndex);
                gl.bindTexture(gl.TEXTURE_CUBE_MAP, SceneBase.TextureEnvMapWithMips);
                gl.uniform1i(uniformsLocation.CubemapTexture, textureBindIndex);
                textureBindIndex++;
            }
            GLSetTexture(gl, uniformsLocation.BRDF_LUT, GBasePass.GInstance!.TextureIntegratedBRDF, textureBindIndex++);
        }

        gl.drawArraysInstanced(gl.TRIANGLES, 0, 6, this.MaxNumInstances);
    }

    constructor(gl: WebGL2RenderingContext, scene: PayneSceneDestrPainting, parentTiles: DestructionTiles) {
        const posBufferCPU = new Float32Array(this.MaxNumInstances * 3);
        posBufferCPU.fill(-100);

        for (let i = 0; i < 2; i++) {
            this.PositionsBufferGPU[i] = CreateBuffer(gl, posBufferCPU, gl.STREAM_DRAW);

            //Update VAO
            {
                this.VAOUpdate[i] = gl.createVertexArray()!;
                gl.bindVertexArray(this.VAOUpdate[i]);
                {
                    DefineVertexAttrib(
                        gl,
                        0,
                        this.PositionsBufferGPU[i],
                        3,
                        gl.FLOAT,
                        3 * Float32Array.BYTES_PER_ELEMENT,
                    );
                }
            }

            this.TransformFeedbackGPU[i] = gl.createTransformFeedback()!;
            gl.bindTransformFeedback(gl.TRANSFORM_FEEDBACK, this.TransformFeedbackGPU[i]);
            gl.bindBufferBase(gl.TRANSFORM_FEEDBACK_BUFFER, 0, this.PositionsBufferGPU[i]);
            gl.bindTransformFeedback(gl.TRANSFORM_FEEDBACK, null);

            //Render VAO
            {
                this.VAORender[i] = gl.createVertexArray()!;
                gl.bindVertexArray(this.VAORender[i]);
                DefineVertexAttrib(gl, 0, this.PositionsBufferGPU[i], 3, gl.FLOAT, 3 * Float32Array.BYTES_PER_ELEMENT);
                gl.vertexAttribDivisor(0, 1);
            }
        }

        gl.bindTransformFeedback(gl.TRANSFORM_FEEDBACK, null);
        gl.bindBuffer(gl.ARRAY_BUFFER, null);
        gl.bindVertexArray(null);

        this.ShaderUpdate = new ShaderTF(
            gl,
            GetDestDecalUpdateShaderVS(),
            ParticleUpdatePS,
            GetBasePassUniformParametersList,
            ["outPosition"],
        );

        this.ShaderRender = new Shader(
            gl,
            GetShaderSourceBasePassDecalRenderVS(),
            GetShaderSourceBasePassDestrRenderPS(
                scene.SceneSDFDescShootingScene!,
                parentTiles.NumTiles,
                parentTiles.TileSize,
                scGetDecalSpecificPSCode(),
            ),
            GetBasePassUniformParametersList,
        );

        {
            this.Material.AlbedoTexture = GTexturePool.CreateTexture(gl, false, `bulletDecalDFS`, true, true);
            this.Material.NormalTexture = GTexturePool.CreateTexture(gl, false, `bulletDecalNRM`, true, true);
            this.Material.RoughnessTexture = GTexturePool.CreateTexture(
                gl,
                false,
                `bulletDecalMask_R8`,
                true,
                true,
                false,
            );
            this.Material.MaskTexture = GTexturePool.CreateTexture(gl, false, `bulletDecalMask_R8`, true, true);
            this.Material.bHasMask = true;
        }
    }
}
